/**
 * Read-only summary of a single RMA — the detail content shown on the
 * Return view page.
 *
 * Shows ICCID links gated by isStaff, staff-only hidden comments,
 * and a staff-only status change control.
 *
 * Contains:
 * - ReturnSummary          — default export, the main content component
 * - DetailRow              — label/value row (local, not imported from tickets)
 * - SectionCard            — titled card wrapper (local, not imported from tickets)
 * - StatusChangeControl    — staff-only inline status picker
 * - RadioSection           — old/new radio display sub-section
 * - AddressSection         — shipping/billing address sub-section
 * - FeesSection            — warranty fee / additional fees / shipping costs
 * - PackingSuppliesSection — inventory adjustments / items shipped
 */

import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { List as IList, Map } from 'immutable';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import { AddressDisplayCard } from '@sophocles/components/contacts';
import MoveInventoryDialog from '@sophocles/components/dialogs/MoveInventoryDialog';
import StatusChip from '@sophocles/components/StatusChip';
import { usePatchRmaMutation } from '@sophocles/features/entities/rmas';
import { addToast } from '@sophocles/features/toast';
import { formatUserString } from '@sophocles/utils';
import InvoiceSummaryPage from '@sophocles/views/returns/components/InvoiceSummaryPage';
import PackingListPage from '@sophocles/views/returns/components/PackingListPage';

import { getStatusLabel, rmaStatusFamily } from '../utils/rmaStatus';
import { returnEntityLabels } from '../utils/tenantLabels';

import { DetailRow, SectionCard } from './ReturnSection';

// ---------------------------------------------------------------------------
// RMA status helpers
// ---------------------------------------------------------------------------

function RmaStatusChip({ status }) {
  const { t } = useTranslation();
  return <StatusChip label={getStatusLabel(t, status)} color={rmaStatusFamily(status)} />;
}

// ---------------------------------------------------------------------------
// Staff-only status change control
// ---------------------------------------------------------------------------

function StatusChangeControl({ rmaId, currentStatus, onRefresh }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [patchRma] = usePatchRmaMutation();
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(String(currentStatus));
  const [saving, setSaving] = useState(false);

  const handleConfirm = () => {
    setSaving(true);
    patchRma({ id: rmaId, body: { status: selectedStatus } })
      .unwrap()
      .then(() => {
        dispatch(
          addToast({
            title: t('general.success', 'Success'),
            message: t('rma.rmaUpdated', 'RMA updated'),
            level: 'success',
            dismissible: true,
          }),
        );
        setOpen(false);
        setSaving(false);
        onRefresh();
      })
      .catch(() => setSaving(false));
  };

  if (!open) {
    return (
      <Button size="small" variant="outlined" onClick={() => setOpen(true)} sx={{ mt: 1 }}>
        {t('rma.changeStatus', 'Change Status')}
      </Button>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>{t('rma.status', 'Status')}</InputLabel>
        <Select
          value={selectedStatus}
          label={t('rma.status', 'Status')}
          onChange={e => setSelectedStatus(e.target.value)}
        >
          <MenuItem value="0">{t('rma.statusIssued', 'Issued')}</MenuItem>
          <MenuItem value="2">{t('rma.statusQuickShipped', 'Quick Shipped')}</MenuItem>
          <MenuItem value="1">{t('rma.statusProcessed', 'Processed & Shipped')}</MenuItem>
          <MenuItem value="100">{t('rma.statusCompleted', 'Completed')}</MenuItem>
          <MenuItem value="9999">{t('rma.statusCanceled', 'Canceled')}</MenuItem>
        </Select>
      </FormControl>
      <Button size="small" variant="contained" onClick={handleConfirm} disabled={saving}>
        {saving ? <CircularProgress size={16} /> : t('general.confirm', 'Confirm')}
      </Button>
      <Button size="small" onClick={() => setOpen(false)}>
        {t('general.cancel', 'Cancel')}
      </Button>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Address helper — mirrors legacy convertAddressToContact exactly
// ---------------------------------------------------------------------------

function buildAddressContact(rma, inlinePrefix, fkAddress) {
  const hasInlineData = inlinePrefix && (rma.get(`${inlinePrefix}_contact`) || rma.get(`${inlinePrefix}_company_name`));

  if (hasInlineData) {
    return {
      id: null,
      name: rma.get(`${inlinePrefix}_contact`, ''),
      business_name: rma.get(`${inlinePrefix}_company_name`, ''),
      email: rma.get(`${inlinePrefix}_email`, ''),
      phone1: rma.get(`${inlinePrefix}_phone`, ''),
      address: rma.get(`${inlinePrefix}_address`, ''),
      address_2: rma.get(`${inlinePrefix}_address_2`, ''),
      city: rma.get(`${inlinePrefix}_city`, ''),
      state: rma.get(`${inlinePrefix}_state`, ''),
      zipcode: rma.get(`${inlinePrefix}_zip`, ''),
      country: rma.get(`${inlinePrefix}_country`, ''),
    };
  }

  if (fkAddress) {
    const addr = fkAddress.toJS ? fkAddress.toJS() : fkAddress;
    return {
      id: addr.id,
      name: addr.name || '',
      business_name: addr.business_name || '',
      email: addr.email || '',
      phone1: addr.phone || '',
      address: addr.address_line_1 || addr.address || '',
      address_2: addr.address_line_2 || '',
      city: addr.city || '',
      state: addr.state || '',
      zipcode: addr.postal_code || addr.zipcode || '',
      country: addr.country || '',
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Radio location helper — mirrors legacy getRadioLocationInfo exactly
// ---------------------------------------------------------------------------

function getRadioLocationInfo(radio) {
  if (!radio) return null;

  if (radio.get('site')) {
    const sg = radio.getIn(['site', 'primary_site_group'], Map());
    return {
      customerName: sg.getIn(['customer', 'id']) + '. ' + sg.getIn(['customer', 'name']),
      plantName: sg.get('description'),
      siteName: radio.getIn(['site', 'name']),
    };
  }
  if (radio.get('site_group')) {
    const sg = radio.get('site_group', Map());
    return {
      customerName: sg.getIn(['customer', 'id']) + '. ' + sg.getIn(['customer', 'name']),
      plantName: sg.get('description'),
    };
  }
  if (radio.get('customer')) {
    return {
      customerName: radio.getIn(['customer', 'id']) + '. ' + radio.getIn(['customer', 'name']),
    };
  }
  if (radio.get('inventory_location')) {
    return { inventoryName: radio.getIn(['inventory_location', 'name']) };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Print menu (staff-only)
//
// "Print Packing List" and "Print Invoice" render a prop-fed print page
// component in place of the summary content then call window.print(),
// mirroring the legacy ViewRmaPage mechanism exactly.
// "Print Result Summary" navigates to the existing routed printable page.
// ---------------------------------------------------------------------------

function PrintMenu({ rmaId, rmaStatus, isStaff, onPrintPackingList, onPrintInvoice }) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const statusAtleastProcessed = rmaStatus === 1 || rmaStatus === 100;
  const statusCompleted = rmaStatus === 100;

  if (!isStaff) return null;

  const hasAnythingToPrint = (rmaStatus === 2 && isStaff) || statusAtleastProcessed;

  const handlePackingList = () => {
    setAnchorEl(null);
    onPrintPackingList();
  };

  const handleInvoice = () => {
    setAnchorEl(null);
    onPrintInvoice();
  };

  return (
    <>
      <IconButton onClick={e => setAnchorEl(e.currentTarget)} size="small" aria-label={t('general.actions', 'Actions')}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
        {!hasAnythingToPrint && <MenuItem disabled>{t('rma.nothingToPrint', 'Nothing to print yet')}</MenuItem>}
        {rmaStatus === 2 && isStaff && (
          <MenuItem onClick={handlePackingList}>{t('rma.printPackingList', 'Print Packing List')}</MenuItem>
        )}
        {statusAtleastProcessed && (
          <MenuItem
            component={Link}
            to={`/rma/${rmaId}/resultsummary`}
            target="_blank"
            onClick={() => setAnchorEl(null)}
          >
            {t('rma.printResultSummary', 'Print Result Summary')}
          </MenuItem>
        )}
        {statusCompleted && isStaff && (
          <MenuItem onClick={handleInvoice}>{t('rma.printInvoice', 'Print Invoice')}</MenuItem>
        )}
      </Menu>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @param {Object} props
 * @param {import('immutable').Map} props.rma - The RMA Immutable Map
 * @param {Function} props.onRefresh - Callback to refetch the RMA
 * @param {string} [props.rmaBaseLink] - Base path for sibling RMA links
 *   (e.g. /rma or /c/:cid/plant/:sid/support/rma). Used when linking to the
 *   shippingCostIncludedOnRma sibling RMA.
 */
export default function ReturnSummary({ rma, onRefresh, rmaBaseLink = '/rma' }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isStaff, permissions } = useUser();
  const canMoveOrAssign = isStaff || permissions.isInventoryAdmin;

  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [printingPackingList, setPrintingPackingList] = useState(false);
  const [printingInvoice, setPrintingInvoice] = useState(false);

  const triggerPrint = setPrintingFlag => {
    setPrintingFlag(true);
    setTimeout(() => {
      window.print();
      setPrintingFlag(false);
    }, 500);
  };

  // --- Core scalars ---
  const rmaId = rma.get('id', null);
  const rmaNumber = rma.get('rma_number', null);
  const rmaStatus = rma.get('status', 0);

  const statusAtleastQuickshipped = rmaStatus > 0;
  const statusAtleastProcessed = rmaStatus === 1 || rmaStatus === 100;

  // --- Customer / SiteGroup / Site ---
  const customer = rma.getIn(['site_group', 'customer'], Map());
  const customerId = customer ? customer.get('id', 0) : 0;
  const entityLabels = returnEntityLabels(t, customer);
  const siteGroup = rma.get('site_group', Map());
  const site = rma.get('site', null);
  const ticket = rma.get('ticket', null);

  // --- Timeline ---
  const issueTime = rma.get('input_time', null);
  const updateTime = rma.get('update_time', null);
  const shippingDate = rma.get('shipping_date', null);
  const receivedDate = rma.get('received_date', null);
  const invoiceDate = rma.get('invoiced_time', null);

  const issueUser = rma.get('input_user', null);
  const updateUser = rma.get('update_user', null);
  const shippingUser = rma.get('shipping_user', null);
  const receivedUser = rma.get('received_user', null);
  const invoiceUser = rma.get('invoiced_user', null);

  // --- Radios ---
  const oldICCID = rma.getIn(['radio_returned_by_user', 'iccid'], '');
  const newICCID = rma.getIn(['radio_shipped_to_user', 'iccid'], '');
  const returnedRadioId = rma.getIn(['radio_returned_by_user', 'id'], null);
  const shippedRadioId = rma.getIn(['radio_shipped_to_user', 'id'], null);
  const legacyOldIccid = rma.get('legacy_incoming_iccid', null);
  const legacyReplacedIccid = rma.get('legacy_replaced_iccid', null);
  const unauthorizedSwap = rma.get('unauthorized_swap', false);
  const firmwareVersionReturnedByUser = rma.get('firmware_version_returned_by_user');
  const modelReturnedByUser = rma.get('model_returned_by_user');
  const arrivalFirmwareVersion = rma.get('arrival_firmware_version');
  const modelNumber = rma.get('model', null);

  // --- Issue category ---
  const issueSubCategory = rma.get('issue', null);
  const issueCategory = issueSubCategory ? issueSubCategory.get('category', null) : null;

  // --- Warranty / fees ---
  const warrantyStatus = rma.getIn(['warranty_status', 'name'], null);
  const warrantyFee = rma.get('warranty_fee', null);
  const additionalFees = rma.get('additional_fees', IList());
  const rawShippingCost = rma.get('shipping_cost', null);
  const rawHandlingCost = rma.get('handling_cost', null);
  const hasCost = rawShippingCost != null || rawHandlingCost != null;
  const shippingCost = Number(rawShippingCost ?? 0);
  const handlingCost = Number(rawHandlingCost ?? 0);
  const shippingCostIncludedOnRma = rma.get('shipping_cost_included_on_rma', null);
  const initialTestResults = rma.getIn(['initial_test_results', 'name'], null);
  const inboundShippingMode = rma.getIn(['inbound_shipping_mode', 'name'], null);
  const outboundShippingMode = rma.getIn(['outbound_shipping_mode', 'name'], null);

  // --- Shipping details ---
  const isQuickShip = rma.get('is_quick_ship', false);
  const trackingNumber = rma.get('tracking_number', '');
  const billingAddress = rma.get('billing_address', null);
  const shippingAddress = rma.get('shipping_address', null);

  const displayShippingContact = buildAddressContact(rma, 'ship', shippingAddress);
  const displayBillingContact = buildAddressContact(rma, 'bill', billingAddress);

  // --- Contact ---
  const legacyContact = rma.get('contact', Map());
  const legacyContactName = legacyContact ? legacyContact.get('name', null) : null;
  const legacyContactTitle = legacyContact ? legacyContact.get('title', null) : null;
  const legacyContactEmail = legacyContact ? legacyContact.get('email', null) : null;
  const supportContactName = rma.get('support_contact_name', '');
  const supportContactEmail = rma.get('support_contact_email', '');
  const supportContactPhone = rma.get('support_contact_phone', '');
  const hasReportingContact = !!(supportContactName || supportContactEmail);
  const hasLegacyContact = !!(legacyContactName || legacyContactEmail);

  // --- Comments / picture ---
  const comments = rma.get('comments', '');
  const hiddenComments = rma.get('hidden_comments', '');
  const picture = rma.get('picture', null);

  // --- Items shipped ---
  const pastInventoryAdjustments = rma.get('inventory_adjustments', IList());

  // --- Radio location info from the returned radio embedded in the RMA response ---
  // Note: unlike the legacy page we read from the nested radio data already on rma
  // rather than a separately-fetched radio object, since fetchRma returns expanded
  // radio_returned_by_user / radio_shipped_to_user objects. For the location display
  // we read those nested objects directly.
  const returnedRadioFull = rma.get('radio_returned_by_user', null);
  const shippedRadioFull = rma.get('radio_shipped_to_user', null);

  const oldRadioLocationInfo =
    returnedRadioFull && returnedRadioFull.get ? getRadioLocationInfo(returnedRadioFull) : null;

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const formatTime = isoString => {
    if (!isoString) return null;
    return new Date(isoString).toLocaleString();
  };

  const formatDate = isoString => {
    if (!isoString) return null;
    return new Date(isoString).toLocaleDateString();
  };

  const warrantyFeeLabel = warrantyFee ? warrantyFee.get('reason', '') : t('rma.noneSelected', 'None selected');

  const shippingCostDisplay = shippingCostIncludedOnRma ? (
    <span>
      {t('rma.includedOnRma', 'Included on RMA')} #{shippingCostIncludedOnRma.get('rma_number', '')}{' '}
      <Link
        to={`${rmaBaseLink}/${shippingCostIncludedOnRma.get('id', 0)}/view`}
        target="_blank"
        style={{ fontSize: '13px' }}
      >
        ({t('general.link', 'link')})
      </Link>
    </span>
  ) : hasCost ? (
    `$${(shippingCost + handlingCost).toFixed(2)}`
  ) : null;

  const trackingDisplay = shippingCostIncludedOnRma ? (
    <span>
      {t('rma.includedOnRma', 'Included on RMA')} #{shippingCostIncludedOnRma.get('rma_number', '')}{' '}
      <Link
        to={`${rmaBaseLink}/${shippingCostIncludedOnRma.get('id', 0)}/view`}
        target="_blank"
        style={{ fontSize: '13px' }}
      >
        ({t('general.link', 'link')})
      </Link>
    </span>
  ) : (
    trackingNumber
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (printingPackingList) {
    return <PackingListPage rma={rma} />;
  }

  if (printingInvoice) {
    return <InvoiceSummaryPage rma={rma} />;
  }

  return (
    <Box>
      {/* ---- Status row + actions ---- */}
      <SectionCard>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <RmaStatusChip status={rmaStatus} />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {rmaStatus === 0 && isStaff && (
              <Button onClick={() => navigate(`/rma/${rmaId}/process`)} variant="contained" size="small">
                {t('rma.process', 'Process')}
              </Button>
            )}
            {isStaff && (
              <Button onClick={() => navigate(`/rma/${rmaId}/issue`)} variant="outlined" size="small">
                {t('general.edit', 'Edit')}
              </Button>
            )}
            <PrintMenu
              rmaId={rmaId}
              rmaStatus={rmaStatus}
              isStaff={isStaff}
              onPrintPackingList={() => triggerPrint(setPrintingPackingList)}
              onPrintInvoice={() => triggerPrint(setPrintingInvoice)}
            />
          </Stack>
        </Stack>

        {isStaff && (
          <Box sx={{ mt: 1 }}>
            <StatusChangeControl rmaId={rmaId} currentStatus={rmaStatus} onRefresh={onRefresh} />
          </Box>
        )}
      </SectionCard>

      {/* ---- Metadata ---- */}
      <SectionCard title={t('rma.details', 'Details')}>
        {isStaff && customer ? (
          <DetailRow label={entityLabels.customer}>
            {customerId}. {customer.get('name', '')}{' '}
            <Link target="_blank" to={`/c/${customerId}/plants`} style={{ fontSize: '13px' }}>
              ({t('general.link', 'link')})
            </Link>
          </DetailRow>
        ) : null}

        {siteGroup && !siteGroup.isEmpty() ? (
          <DetailRow label={entityLabels.siteGroup}>
            {siteGroup.get('name', '')}. {siteGroup.get('description', '')}{' '}
            {isStaff && (
              <Link
                target="_blank"
                to={`/c/${customerId}/plant/${siteGroup.get('id', 0)}/structures`}
                style={{ fontSize: '13px' }}
              >
                ({t('general.link', 'link')})
              </Link>
            )}
          </DetailRow>
        ) : null}

        {site ? (
          <DetailRow label={entityLabels.structure}>
            {site.get('name', '')}. {site.get('description', '')}{' '}
            {isStaff && (
              <Link
                target="_blank"
                to={`/c/${customerId}/plant/${siteGroup.get('id', 0)}/structure/${site.get('id', 0)}`}
                style={{ fontSize: '13px' }}
              >
                ({t('general.link', 'link')})
              </Link>
            )}
          </DetailRow>
        ) : null}

        {ticket ? (
          <DetailRow label={t('rma.supportTicket', 'Support Ticket')}>
            {ticket.get('ticket_number', '')}{' '}
            {isStaff && (
              <Link target="_blank" to={`/support/ticket/${ticket.get('id')}/view`} style={{ fontSize: '13px' }}>
                ({t('general.link', 'link')})
              </Link>
            )}
          </DetailRow>
        ) : null}

        {warrantyStatus ? <DetailRow label={t('rma.warrantyStatus', 'Warranty')} value={warrantyStatus} /> : null}

        {issueCategory ? (
          <DetailRow label={t('rma.issueCategory', 'Category')} value={issueCategory.get('name', '')} />
        ) : null}

        {issueSubCategory ? (
          <DetailRow label={t('rma.issueSubCategory', 'Subcategory')} value={issueSubCategory.get('name', '')} />
        ) : null}
      </SectionCard>

      {/* ---- Timeline ---- */}
      <SectionCard title={t('rma.timeline', 'Timeline')}>
        {issueTime ? (
          <DetailRow label={t('rma.timeIssued', 'Issued')}>
            {formatTime(issueTime)}
            {isStaff && issueUser ? ` — ${formatUserString(issueUser)}` : ''}
          </DetailRow>
        ) : null}
        {shippingDate ? (
          <DetailRow label={t('rma.dateShipped', 'Shipped')}>
            {formatDate(shippingDate)}
            {isStaff && shippingUser ? ` — ${formatUserString(shippingUser)}` : ''}
          </DetailRow>
        ) : null}
        {receivedDate ? (
          <DetailRow label={t('rma.dateProcessed', 'Processed')}>
            {formatDate(receivedDate)}
            {isStaff && receivedUser ? ` — ${formatUserString(receivedUser)}` : ''}
          </DetailRow>
        ) : null}
        {invoiceDate && isStaff ? (
          <DetailRow label={t('rma.dateInvoiced', 'Invoiced')}>
            {formatDate(invoiceDate)}
            {invoiceUser ? ` — ${formatUserString(invoiceUser)}` : ''}
          </DetailRow>
        ) : null}
        {updateTime && isStaff ? (
          <DetailRow label={t('rma.lastUpdated', 'Last Updated')}>
            {formatTime(updateTime)}
            {updateUser ? ` — ${formatUserString(updateUser)}` : ''}
          </DetailRow>
        ) : null}
      </SectionCard>

      {/* ---- Device info ---- */}
      <SectionCard title={t('rma.deviceInfo', 'Device Info')}>
        {returnedRadioId ? (
          <DetailRow label={t('rma.returnDevice', 'Return Device')}>
            {isStaff ? (
              <Link to={`/support/radios/${returnedRadioId}`}>{oldICCID || legacyOldIccid}</Link>
            ) : (
              oldICCID || legacyOldIccid
            )}
          </DetailRow>
        ) : null}

        {returnedRadioFull && oldRadioLocationInfo ? (
          <DetailRow label={t('rma.radioLocation', 'Location')}>
            <Box>
              {oldRadioLocationInfo.siteName != null ? (
                <>
                  <div>
                    {t('general.customer', 'Customer')}: {oldRadioLocationInfo.customerName}
                  </div>
                  <div>
                    {t('general.siteGroup', 'Site Group')}: {oldRadioLocationInfo.plantName}
                  </div>
                  <div>
                    {t('general.site', 'Site')}: {oldRadioLocationInfo.siteName}
                  </div>
                </>
              ) : oldRadioLocationInfo.plantName != null ? (
                <>
                  <div>
                    {t('general.customer', 'Customer')}: {oldRadioLocationInfo.customerName}
                  </div>
                  <div>
                    {t('general.siteGroup', 'Site Group')}: {oldRadioLocationInfo.plantName}
                  </div>
                </>
              ) : oldRadioLocationInfo.customerName != null ? (
                <div>
                  {t('general.customer', 'Customer')}: {oldRadioLocationInfo.customerName}
                </div>
              ) : oldRadioLocationInfo.inventoryName != null ? (
                <div>
                  {t('rma.inventory', 'Inventory')}: {oldRadioLocationInfo.inventoryName}
                </div>
              ) : null}
            </Box>
          </DetailRow>
        ) : null}

        {canMoveOrAssign && statusAtleastProcessed && returnedRadioFull ? (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                onClick={() => {
                  setIsArchiving(true);
                  setIsMoveDialogOpen(true);
                }}
                disabled={
                  isArchiving ||
                  isMoveDialogOpen ||
                  returnedRadioFull.getIn(['location', 'inventory_location', 'id']) === 2
                }
              >
                {returnedRadioFull.getIn(['location', 'inventory_location', 'id']) === 2
                  ? t('rma.archived', 'Archived')
                  : t('rma.archive', 'Archive')}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setIsArchiving(false);
                  setIsMoveDialogOpen(true);
                }}
                disabled={isArchiving || isMoveDialogOpen}
              >
                {t('rma.move', 'Move')}
              </Button>
            </Stack>
            <MoveInventoryDialog
              open={isArchiving || isMoveDialogOpen}
              customerId={customerId}
              radioLocationId={returnedRadioFull.getIn(['location', 'id'])}
              radioBillingId={returnedRadioFull.getIn(['billing', 'id'])}
              inventoryLocationId={isArchiving ? 2 : null}
              targetSiteGroups={!isArchiving}
              onSubmit={() => onRefresh()}
              onClose={() => {
                setIsMoveDialogOpen(false);
                setIsArchiving(false);
              }}
            />
          </Box>
        ) : null}

        <DetailRow
          label={t('rma.authorizedSwap', 'Authorized Swap')}
          value={unauthorizedSwap ? t('general.no', 'No') : t('general.yes', 'Yes')}
        />

        {statusAtleastProcessed && arrivalFirmwareVersion ? (
          <DetailRow label={t('rma.arrivalFirmware', 'Arrival Firmware')} value={arrivalFirmwareVersion} />
        ) : null}

        {statusAtleastProcessed && modelNumber ? (
          <DetailRow label={t('rma.modelNumber', 'Model #')} value={modelNumber} />
        ) : null}

        {firmwareVersionReturnedByUser ? (
          <DetailRow
            label={t('rma.firmwareReturnedByUser', 'Firmware Returned By User')}
            value={firmwareVersionReturnedByUser}
          />
        ) : null}

        {modelReturnedByUser ? (
          <DetailRow label={t('rma.modelReturnedByUser', 'Model Returned By User')} value={modelReturnedByUser} />
        ) : null}

        {isStaff && statusAtleastProcessed && initialTestResults ? (
          <DetailRow label={t('rma.initialTestResults', 'Initial Test Results')} value={initialTestResults} />
        ) : null}

        {statusAtleastQuickshipped && shippedRadioId ? (
          <DetailRow label={t('rma.replacementDevice', 'Replacement Device')}>
            {isStaff ? (
              <Link to={`/support/radios/${shippedRadioId}`}>{newICCID || legacyReplacedIccid}</Link>
            ) : (
              newICCID || legacyReplacedIccid
            )}
          </DetailRow>
        ) : null}

        <Divider sx={{ my: 1 }} />

        {statusAtleastProcessed ? (
          <DetailRow
            label={t('rma.warrantyStatus', 'Warranty')}
            value={warrantyStatus || t('rma.noneSelected', 'None selected')}
          />
        ) : null}

        {statusAtleastProcessed ? (
          <DetailRow label={t('rma.warrantyFee', 'Warranty Fee')} value={warrantyFeeLabel} />
        ) : null}
      </SectionCard>

      {/* ---- Reporting contact ---- */}
      <SectionCard title={t('rma.reportingContact', 'Reporting Contact')}>
        {!hasReportingContact && !hasLegacyContact ? (
          <Typography variant="body2" color="text.disabled">
            {t('rma.noContactSelected', 'No Contact Selected')}
          </Typography>
        ) : null}
        {hasReportingContact ? (
          <>
            {supportContactName ? <DetailRow label={t('general.name', 'Name')} value={supportContactName} /> : null}
            {supportContactEmail ? <DetailRow label={t('general.email', 'Email')} value={supportContactEmail} /> : null}
            {supportContactPhone ? <DetailRow label={t('general.phone', 'Phone')} value={supportContactPhone} /> : null}
          </>
        ) : null}
        {!hasReportingContact && hasLegacyContact ? (
          <>
            {legacyContactName ? <DetailRow label={t('general.name', 'Name')} value={legacyContactName} /> : null}
            {legacyContactTitle ? <DetailRow label={t('general.title', 'Title')} value={legacyContactTitle} /> : null}
            {legacyContactEmail ? <DetailRow label={t('general.email', 'Email')} value={legacyContactEmail} /> : null}
          </>
        ) : null}
      </SectionCard>

      {/* ---- Shipping details ---- */}
      <SectionCard title={t('rma.shippingDetails', 'Shipping Details')}>
        <AddressDisplayCard
          label={t('rma.shippingAddress', 'Shipping Address')}
          contact={displayShippingContact}
          showAddress
          addressOnlyMode
          disabled
        />
        <DetailRow
          label={t('rma.quickShipLabel', 'Quick Ship RMA?')}
          value={isQuickShip ? t('general.yes', 'Yes') : t('general.no', 'No')}
        />
        {statusAtleastQuickshipped ? (
          <DetailRow label={t('rma.trackingNumber', 'Tracking Number')}>{trackingDisplay}</DetailRow>
        ) : null}
        {inboundShippingMode ? (
          <DetailRow label={t('rma.inboundInstructions', 'Inbound Instructions')} value={inboundShippingMode} />
        ) : null}
        {outboundShippingMode ? (
          <DetailRow label={t('rma.outboundInstructions', 'Outbound Instructions')} value={outboundShippingMode} />
        ) : null}
      </SectionCard>

      {/* ---- Billing details ---- */}
      <SectionCard title={t('rma.billingDetails', 'Billing Details')}>
        <AddressDisplayCard
          label={t('rma.billingAddress', 'Billing Address')}
          contact={displayBillingContact}
          showAddress
          addressOnlyMode
          disabled
        />
        {statusAtleastProcessed ? (
          <DetailRow label={t('rma.warrantyFee', 'Warranty Fee')} value={warrantyFeeLabel} />
        ) : null}
        {statusAtleastQuickshipped ? (
          <DetailRow label={t('rma.additionalFees', 'Additional Fees')}>
            {additionalFees.size > 0 ? (
              <ul style={{ margin: 0, listStyle: 'none', padding: 0 }}>
                {additionalFees.map((fee, index) => (
                  <li key={index}>{fee.get('reason', '')}</li>
                ))}
              </ul>
            ) : (
              t('rma.noExtraFees', 'No Extra Fees')
            )}
          </DetailRow>
        ) : null}
        {statusAtleastQuickshipped ? (
          <DetailRow label={t('rma.shippingHandlingCost', 'Shipping & Handling')}>{shippingCostDisplay}</DetailRow>
        ) : null}
      </SectionCard>

      {/* ---- Items shipped ---- */}
      {statusAtleastQuickshipped ? (
        <SectionCard title={t('rma.itemsShipped', 'Items Shipped')}>
          {pastInventoryAdjustments.isEmpty() ? (
            <Typography variant="body2" color="text.disabled">
              {t('rma.noneSelected', 'None Selected')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {isStaff
                ? pastInventoryAdjustments.map((item, index) => (
                    <ListItem key={index} disableGutters disablePadding>
                      <ListItemText
                        primary={`${item.getIn(['product', 'item_part_num'], '')} — ${item.getIn(['product', 'item_name'], '')} — ${
                          item.get('quantity_shipped', 0) > 0
                            ? t('rma.removedQty', 'Removed: {{qty}}', { qty: item.get('quantity_shipped', 0) })
                            : t('rma.addedColonQty', 'Added: {{qty}}', {
                                qty: item.get('quantity_added_to_inventory', 0),
                              })
                        }`}
                      />
                    </ListItem>
                  ))
                : pastInventoryAdjustments
                    .filterNot(item => item.get('packing_supply', true))
                    .filter(item => item.get('quantity_shipped', 0) > 0)
                    .map((item, index) => (
                      <ListItem key={index} disableGutters disablePadding>
                        <ListItemText
                          primary={`${item.get('quantity_shipped', 0)}x — ${item.getIn(['product', 'item_part_num'], '')} — ${item.getIn(['product', 'item_name'], '')}`}
                        />
                      </ListItem>
                    ))}
            </List>
          )}
        </SectionCard>
      ) : null}

      {/* ---- Comments ---- */}
      <SectionCard title={t('rma.comments', 'Comments')}>
        <DetailRow label={t('rma.resolution', 'Resolution')} value={comments || '—'} />
        {isStaff ? (
          <DetailRow label={t('rma.hiddenComments', 'Hidden Comments')} value={hiddenComments || '—'} />
        ) : null}
      </SectionCard>

      {/* ---- Picture ---- */}
      {picture ? (
        <SectionCard title={t('rma.picture', 'RMA Picture')}>
          <Box sx={{ textAlign: 'center' }}>
            <img src={picture} style={{ maxWidth: '100%', maxHeight: 400 }} alt={t('rma.picture', 'RMA Picture')} />
          </Box>
        </SectionCard>
      ) : null}
    </Box>
  );
}
